import { Control, Controller, FieldErrors } from 'react-hook-form';
import { GroupTemplateFormData } from './group-template-form-data';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

interface StepOneProps {
  control: Control<GroupTemplateFormData>;
  errors: FieldErrors<GroupTemplateFormData>;
}

export default function StepOne({ control, errors }: StepOneProps) {
  const primaryLanguageOptions = [
    { displayValue: 'Arabic', value: 'Arabic' },
    { displayValue: 'Bengali', value: 'Bengali' },
    { displayValue: 'Bulgarian', value: 'Bulgarian' },
    { displayValue: 'Catalan', value: 'Catalan' },
    { displayValue: 'Chinese - Simplified', value: 'Chinese_Simplified' },
    { displayValue: 'Chinese - Traditional', value: 'Chinese_Traditional' },
    { displayValue: 'Croatian', value: 'Croatian' },
    { displayValue: 'Czech', value: 'Czech' },
    { displayValue: 'Danish', value: 'Danish' },
    { displayValue: 'Dutch', value: 'Dutch' },
    { displayValue: 'English - United States', value: 'English_United_States' },
    {
      displayValue: 'English - United Kingdom',
      value: 'English_United_Kingdom',
    },
    { displayValue: 'Estonian', value: 'Estonian' },
    { displayValue: 'Finnish', value: 'Finnish' },
    { displayValue: 'French', value: 'French' },
    { displayValue: 'German', value: 'German' },
    { displayValue: 'Greek', value: 'Greek' },
    { displayValue: 'Gujarati', value: 'Gujarati' },
    { displayValue: 'Hebrew', value: 'Hebrew' },
    { displayValue: 'Hindi', value: 'Hindi' },
    { displayValue: 'Hungarian', value: 'Hungarian' },
    { displayValue: 'Icelandic', value: 'Icelandic' },
    { displayValue: 'Indonesian', value: 'Indonesian' },
    { displayValue: 'Italian', value: 'Italian' },
    { displayValue: 'Japanese', value: 'Japanese' },
    { displayValue: 'Kannada', value: 'Kannada' },
    { displayValue: 'Korean', value: 'Korean' },
    { displayValue: 'Latvian', value: 'Latvian' },
    { displayValue: 'Lithuanian', value: 'Lituanian' },
    { displayValue: 'Malay', value: 'Malay' },
    { displayValue: 'Malayalam', value: 'Malayalam' },
    { displayValue: 'Marathi', value: 'Marathi' },
    { displayValue: 'Norwegian', value: 'Norewegian' },
    { displayValue: 'Oriya', value: 'Oriya' },
    { displayValue: 'Persian', value: 'Persian' },
    { displayValue: 'Polish', value: 'Polish' },
    { displayValue: 'Portuguese - Brazil', value: 'Portuguese_Brazil' },
    { displayValue: 'Portuguese - Portugal', value: 'Portuguese_Portugal' },
    { displayValue: 'Romanian', value: 'Romanian' },
    { displayValue: 'Russian', value: 'Russian' },
    { displayValue: 'Serbian', value: 'Serbian' },
    { displayValue: 'Slovak', value: 'Slovak' },
    { displayValue: 'Slovenian', value: 'Slovenian' },
    { displayValue: 'Spanish', value: 'Spanish' },
    { displayValue: 'Swedish', value: 'Swedish' },
    { displayValue: 'Tagalog', value: 'Tagalog' },
    { displayValue: 'Tamil', value: 'Tamil' },
    { displayValue: 'Telugu', value: 'Telugu' },
    { displayValue: 'Thai', value: 'Thai' },
    { displayValue: 'Turkish', value: 'Turkish' },
    { displayValue: 'Ukrainian', value: 'Ukranian' },
    { displayValue: 'Urdu', value: 'Urdu' },
    { displayValue: 'Vietnamese', value: 'Vietnamese' },
  ];

  return (
    <Box sx={{ viewTransitionName: 'steps' }}>
      <Stack gap={2}>
        <Stack flexDirection="row" sx={{ pt: 2, pl: 4 }} gap={14}>
          <Controller
            name="groupTemplateName"
            control={control}
            rules={{
              required: 'Name is required.',
            }}
            render={({ field }) => (
              <TextField
                required
                autoComplete="off"
                inputProps={{
                  id: 'groupTemplateName',
                }}
                InputLabelProps={{
                  htmlFor: 'groupTemplateName',
                }}
                error={!!errors.groupTemplateName}
                helperText={errors?.groupTemplateName?.message || ' '}
                label="Template Name"
                sx={{ width: 400 }}
                value={field.value || ''}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="groupTemplateDesc"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'groupTemplateDesc',
                }}
                InputLabelProps={{
                  htmlFor: 'groupTemplateDesc',
                }}
                label="Template Description"
                sx={{ width: 400 }}
                value={field.value || ''}
                onChange={field.onChange}
              />
            )}
          />
        </Stack>
        <Stack flexDirection="row" sx={{ pl: 4, pb: 1 }} gap={14}>
          <Controller
            name="primaryLanguage"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'primaryLanguage',
                }}
                InputLabelProps={{
                  htmlFor: 'primaryLanguage',
                }}
                select
                label="Primary Language"
                helperText={
                  'This is the language that emails from the Google groups service will be sent in.'
                }
                sx={{ width: 400 }}
                value={field.value || ''}
                onChange={field.onChange}
              >
                {primaryLanguageOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="allowWebPosting"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label="Allow users to post to the group on the web"
              />
            )}
          />
        </Stack>
      </Stack>
      <Divider sx={{ my: 2, mr: 4 }} />
      <Stack sx={{ px: 4, pb: 1 }} gap={1}>
        <Typography variant="body2" sx={{ fontWeight: 300 }}>
          This option includes the group in your Google Apps Global Address
          List. When included in the list, the group becomes available to Google
          Apps Sync for Microsoft Outlook users.
        </Typography>
        <Controller
          name="includeInGlobalAddressList"
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...props}
                  checked={props.value}
                  onChange={(e) => props.onChange(e.target.checked)}
                />
              }
              label="Include this group in Global Address Lists"
            />
          )}
        />
      </Stack>
      <Divider sx={{ my: 2, mr: 4 }} />
      <Stack
        sx={{
          px: 4,
          pb: 1,
        }}
        gap={1}
      >
        <Typography variant="body2" sx={{ fontWeight: 300 }}>
          When this option is unchecked, messages sent to the group will not be
          archived. Previously received messages will remain archived. Only
          archived messages are visible through the web interface.
        </Typography>
        <Controller
          name="isArchived"
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...props}
                  checked={props.value}
                  onChange={(e) => props.onChange(e.target.checked)}
                />
              }
              label="Archive messages to the group"
            />
          )}
        />
      </Stack>
    </Box>
  );
}
